import * as utilityActions from "../actions/utility-actions";
import { IFilterBar } from "../models/utilities/filter-bar";
import { PagedApiResult } from "../models/utilities/paged-request";
import { ToastMessage } from "../models/utilities/toast-message";
import { NotificationType } from "../models/utilities/type-models";
import { getApplicationStore } from "../store/configure-store";
import { Api, ApiResponse, StatusCode } from "../utils/api";
import { AppUtils } from "../utils/app-utils";

export class BaseService {

    protected store = getApplicationStore();

    /**
     * Use the provided filter bar to query an endpoint and
     * return the typed data
     * @param params The search parameters to influence the search by 
     */
    public search = async <T> (toClass : { new(): T },  params : IFilterBar, suppressLoading : boolean = false)  : Promise<ApiResponse<PagedApiResult<T>>> => {

        const response = new ApiResponse<PagedApiResult<T>>();
        
        if (!suppressLoading) {
            this._showLoading();  
        }  

        if (params) {

            try {
                const result = await Api.PagedGet<T>(
                    toClass,
                    AppUtils.convertFiltersToUrl(params.baseUrl, params),
                    params.paginationInfo
                );

                if (result) {
                    response.data = result;
                    response.status = StatusCode.Success;
                }
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }
    
        if (!suppressLoading) {
            this._hideLoading();
        }
        return response;
    }

    /**
     * Get the entity for the ID
     * @param entityId The entity ID to go fetch
     */
    public _get = async <T> (toClass : { new(): T }, entityId: number|string, url : string) : Promise<ApiResponse<T>> => {

        const response = new ApiResponse<T>();
        this._showLoading();    

        if (entityId) {

            try {

                const pickup = await Api.Get<T>(
                    toClass, 
                    url.formattify(entityId)
                );
                
                if (pickup) {
                    response.data = pickup;
                    response.status = StatusCode.Success;
                }
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get the entity for the ID and map it using a function
     * @param entityId The entity ID to go fetch
     */
    public _mappedGet = async <T> (mapper : any, mapperProperty : string, entityId: number, url : string) : Promise<ApiResponse<T>> => {

        const response = new ApiResponse<T>();
        this._showLoading();    

        if (entityId) {

            try {

                const pickup = await Api.MappedGet(
                    mapper, 
                    mapperProperty,
                    url.formattify(entityId)
                );
                
                if (pickup) {
                    response.data = pickup;
                    response.status = StatusCode.Success;
                }
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }

        this._hideLoading();
        return response;
    }

    /**
     * Make a POST
     * @param entityId The entity ID to go fetch
     */
    public _post = async <T> (url : string, payload : any) : Promise<ApiResponse<T>> => {

        const response = new ApiResponse<T>();
        this._showLoading();

        try {

            const result = await Api.Post(
                url, 
                payload
            );

            if (result) {
                response.data = result;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }  

        this._hideLoading();
        return response;
    }

    /**
     * Make a POST and return error messages instead of toasting
     * @param entityId The entity ID to go fetch
     */
     public _postWithError = async <T> (url : string, payload : any) : Promise<ApiResponse<T>> => {

        const response = new ApiResponse<T>();
        
        try {

            const result = await Api.Post(
                url, 
                payload
            );

            if (result) {
                response.data = result;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err, true);
            response.data = AppUtils.FormatError(err) as any;
        }  

        return response;
    }

    /**
     * Make a PUT request with the API client
     * @param endpoint The URL to POST to
     * @param payload The JSON object to POST
     */
    public _put = async <T> (endpoint : string, payload : any) : Promise<ApiResponse<T>> => {

        const response = new ApiResponse<T>();
        this._showLoading();    

        if (endpoint && endpoint.length > 0) {

            try {

                const result = await Api.Put(
                    endpoint,
                    payload
                );

                response.data = result;
                response.status = StatusCode.Success;
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }

        this._hideLoading();
        return response;
    }

    protected _showLoading = () => {
        this.store.dispatch(utilityActions.showLoading());
    }

    protected _hideLoading = () => {
        this.store.dispatch(utilityActions.hideLoading());
    }

    protected _showToast = (message : string, toastType? : NotificationType, durationMs? : number) => {
        toastType = toastType ? toastType : 'error';

        this.store.dispatch(
            utilityActions.showToastNotification(new ToastMessage(message, toastType, durationMs))
        );
    }

    protected _handleError = (err : Error, hideToast? : boolean) : number => {
        console.error(err);
        const errorMsg = AppUtils.FormatError(err);

        if (!hideToast) {
            this.store.dispatch(
                utilityActions.showToastNotification(new ToastMessage(errorMsg, "error"))
            );
        }

        return (err as any).statusCode;
    }

    /**
     * Get the entity for the ID
     * @param entityId The entity ID to go fetch
     */
    protected _delete = async <T> (endpoint : string, payload? : any) : Promise<ApiResponse<T>> => {

        const response = new ApiResponse<T>();
        this._showLoading();    

        if (endpoint && endpoint.length > 0) {

            try {

                await Api.Delete(
                    endpoint,
                    payload
                );
                                
                response.status = StatusCode.Success;
            }
            catch (err) {
                response.status = this._handleError(err);
            }
        }

        this._hideLoading();
        return response;
    }

}
